import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useMemo, useState } from "react";
import { useProject } from "@/lib/project";
import type { TaskListItem } from "@/lib/types";
import { cn, positionBetween } from "@/lib/utils";
import { Spinner } from "../ui";
import { useTaskActions } from "./hooks";
import { TaskCard } from "./TaskCard";
import { groupIdsOf, groupsFor, NONE, type Group, type ViewState } from "./viewState";

const SEP = "::";
const itemId = (group: string, task: string) => `${group}${SEP}${task}`;
const taskOf = (id: string) => id.split(SEP)[1];

type Columns = Record<string, string[]>; // group id -> item ids

function SortableCard({ id, task, onOpen, disabled, epicTitle, showEpic }: { id: string; task: TaskListItem; onOpen: () => void; disabled: boolean; epicTitle?: string; showEpic?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }} {...attributes} {...listeners}>
      <TaskCard task={task} onOpen={onOpen} dragging={isDragging} epicTitle={epicTitle} showEpic={showEpic} />
    </div>
  );
}

function Column({
  group,
  ids,
  tasksById,
  onOpen,
  onCreate,
  onAddClick,
  canDrag,
  epicTitles,
}: {
  group: Group;
  ids: string[];
  tasksById: Map<string, TaskListItem>;
  onOpen: (id: string) => void;
  onCreate?: (groupId: string, title: string) => Promise<void>;
  onAddClick?: (groupId: string) => void;
  canDrag: boolean;
  epicTitles?: Map<string, string>;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: group.id });
  const { canEdit } = useProject();
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const v = title.trim();
    if (!v || !onCreate) return;
    setSaving(true);
    try {
      await onCreate(group.id, v);
      setTitle("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cn("flex w-[272px] shrink-0 flex-col rounded-xl p-2", `col-${group.color}`, isOver && "ring-2 ring-accent/40")}>
      <div className="mb-2 flex items-center gap-2 px-1">
        <span className={cn("tag rounded-full ps-1.5 pe-2 font-medium", `tag-${group.color}`)}>
          <span className={cn("me-1.5 inline-block size-2 rounded-full", `dot-${group.color}`)} />
          <span className="truncate" dir="auto">
            {group.label}
          </span>
        </span>
        <span className="text-xs text-muted tabular-nums">{ids.length}</span>
      </div>
      <SortableContext id={group.id} items={ids} strategy={verticalListSortingStrategy}>
        <div ref={setNodeRef} className="flex min-h-12 flex-col gap-2">
          {ids.map((id) => {
            const task = tasksById.get(taskOf(id));
            return task ? (
              <SortableCard key={id} id={id} task={task} onOpen={() => onOpen(task.id)} disabled={!canDrag} epicTitle={epicTitles?.get(task.epicId)} showEpic={!!epicTitles} />
            ) : null;
          })}
        </div>
      </SortableContext>
      {canEdit && (onCreate || onAddClick) && (
        <div className="mt-2">
          {adding ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
            >
              <textarea
                autoFocus
                rows={2}
                value={title}
                readOnly={saving}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => !title.trim() && setAdding(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                  if (e.key === "Escape") {
                    setTitle("");
                    setAdding(false);
                  }
                }}
                placeholder="Task title… (Enter to add)"
                className="field resize-none bg-surface"
                dir="auto"
              />
              {saving && <Spinner className="size-3.5" />}
            </form>
          ) : (
            <button
              type="button"
              onClick={() => (onCreate ? setAdding(true) : onAddClick?.(group.id))}
              className="flex h-8 w-full items-center gap-1.5 rounded-lg px-2 text-sm text-muted hover:bg-hover"
            >
              <PlusIcon className="size-4" /> New task
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function Board({
  tasks,
  state,
  onOpen,
  onCreate,
  onAddClick,
  epicTitles,
}: {
  tasks: TaskListItem[];
  state: ViewState;
  onOpen: (id: string) => void;
  onCreate?: (groupId: string, title: string) => Promise<void>;
  onAddClick?: (groupId: string) => void;
  epicTitles?: Map<string, string>;
}) {
  const ctx = useProject();
  const { update, setValue } = useTaskActions();
  const groups = useMemo(() => groupsFor(ctx, state.groupBy), [ctx, state.groupBy]);
  const tasksById = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const groupProp = ctx.bundle.properties.find((p) => p.id === state.groupBy);

  const derived = useMemo<Columns>(() => {
    const cols: Columns = Object.fromEntries(groups.map((g) => [g.id, []]));
    for (const t of tasks) {
      for (const g of groupIdsOf(t, state.groupBy, ctx)) {
        if (cols[g]) cols[g].push(itemId(g, t.id));
      }
    }
    return cols;
  }, [tasks, groups, state.groupBy, ctx]);

  const [dragCols, setDragCols] = useState<Columns | null>(null);
  const [active, setActive] = useState<{ id: string; from: string } | null>(null);
  const columns = dragCols ?? derived;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const containerOf = (cols: Columns, id: string) => (id in cols ? id : Object.keys(cols).find((g) => cols[g].includes(id)));

  const onDragStart = ({ active }: DragStartEvent) => {
    const id = String(active.id);
    setDragCols(derived);
    setActive({ id, from: containerOf(derived, id)! });
  };

  const onDragOver = ({ active, over }: DragOverEvent) => {
    if (!over || !dragCols) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    const from = containerOf(dragCols, activeId);
    const to = containerOf(dragCols, overId);
    if (!from || !to || from === to) return;
    setDragCols((cols) => {
      if (!cols) return cols;
      const taskId = taskOf(activeId);
      // a multi-select card may already sit in the target column
      if (cols[to].some((id) => taskOf(id) === taskId)) return cols;
      const target = [...cols[to]];
      const overIndex = target.indexOf(overId);
      target.splice(overIndex >= 0 ? overIndex : target.length, 0, activeId);
      return { ...cols, [from]: cols[from].filter((id) => id !== activeId), [to]: target };
    });
  };

  const onDragEnd = ({ active: a, over }: DragEndEvent) => {
    const cols = dragCols;
    const start = active;
    setActive(null);
    if (!over || !cols || !start) {
      setDragCols(null);
      return;
    }
    const activeId = String(a.id);
    const overId = String(over.id);
    const to = containerOf(cols, activeId);
    if (!to) {
      setDragCols(null);
      return;
    }
    let list = cols[to];
    const oldIndex = list.indexOf(activeId);
    const overIndex = list.indexOf(overId);
    if (overIndex >= 0 && overIndex !== oldIndex) {
      list = [...list];
      list.splice(oldIndex, 1);
      list.splice(overIndex, 0, activeId);
    }
    const task = tasksById.get(taskOf(activeId));
    if (!task) {
      setDragCols(null);
      return;
    }
    const index = list.indexOf(activeId);
    const manual = state.sort.key === "manual";
    const neighbour = (i: number) => (i >= 0 && i < list.length ? tasksById.get(taskOf(list[i]))?.position : undefined);
    const position = manual ? positionBetween(neighbour(index - 1), neighbour(index + 1)) : task.position;
    const moved = to !== start.from;

    // keep the dragged layout until the optimistic cache update lands
    setDragCols({ ...cols, [to]: list });
    const done = () => setDragCols(null);

    if (state.groupBy === "stage" || !groupProp) {
      if (!moved && (!manual || position === task.position)) return done();
      // the optimistic cache patch happens synchronously inside update()
      void update(task.id, moved ? { stageId: to, position } : { position });
      return done();
    }
    if (moved) {
      const current = task.values[groupProp.id] ?? [];
      let next: string[];
      if (groupProp.type === "multi_select") {
        next = current.filter((v) => v !== start.from);
        if (to !== NONE && !next.includes(to)) next.push(to);
      } else {
        next = to === NONE ? [] : [to];
      }
      void setValue(task.id, groupProp.id, next);
    }
    if (manual && position !== task.position) void update(task.id, { position });
    done();
  };

  const activeTask = active ? tasksById.get(taskOf(active.id)) : undefined;
  const visibleGroups = state.hideEmpty ? groups.filter((g) => columns[g.id]?.length) : groups;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setActive(null);
        setDragCols(null);
      }}
    >
      <div className="flex items-start gap-3 overflow-x-auto px-6 pb-8 md:px-12 scroll-thin">
        {visibleGroups.map((g) => (
          <Column
            key={g.id}
            group={g}
            ids={columns[g.id] ?? []}
            tasksById={tasksById}
            onOpen={onOpen}
            onCreate={onCreate}
            onAddClick={onAddClick}
            canDrag={ctx.canEdit}
            epicTitles={epicTitles}
          />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>{activeTask ? <TaskCard task={activeTask} overlay /> : null}</DragOverlay>
    </DndContext>
  );
}
